import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

@Injectable({ providedIn: 'root' })
export class TaxCategoryService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  private mapItem(it: any): TaxCategory {
    const typeOfTaxpayers: TaxCategoryTaxpayer[] = Array.isArray(
      it?.typeOfTaxpayers,
    )
      ? it.typeOfTaxpayers
          .filter((tp: any) => tp?.id != null)
          .map((tp: any) => ({
            id: String(tp.id),
            prefix: tp.prefix || '',
            name: tp.name || tp.prefix || '',
          }))
      : [];

    const typeOfTaxpayerIds =
      typeOfTaxpayers.length > 0
        ? typeOfTaxpayers.map((tp) => tp.id)
        : Array.isArray(it?.typeOfTaxpayerIds)
          ? it.typeOfTaxpayerIds.map((id: unknown) => String(id))
          : [];

    return {
      id: it.id,
      name: it.name || it.title || '',
      relations: it.relations ?? typeOfTaxpayerIds.length,
      typeOfTaxpayers,
      typeOfTaxpayerIds,
      status: this.normalizeStatus(it.status),
      createdAt: it.createdAt || it.created_at || '',
    };
  }

  /**
   * LISTA: Tax categories (server-side pagination)
   */
  getTaxCategories(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<TaxCategoryPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim().toUpperCase());
    }

    const url = `${this.apiUrl}/${countryId}/tax-categories`;
    return this.httpClient.get<ApiResponse<PageableData>>(url, { params }).pipe(
      map((response) => {
        const pageableData = response.data;
        const items = pageableData?.content || [];
        return {
          items: items.map((it: any) => this.mapItem(it)),
          page: pageableData?.page ?? 0,
          size: pageableData?.size ?? size,
          totalElements: pageableData?.totalElements ?? 0,
          totalPages: pageableData?.totalPages ?? 0,
        } as TaxCategoryPageResponse;
      }),
    );
  }

  getTaxCategoryById(countryId: string, id: string | number) {
    const url = `${this.apiUrl}/${countryId}/tax-categories/${id}`;
    return this.httpClient.get<ApiResponse<any>>(url).pipe(
      map((resp) => this.mapItem(resp.data)),
    );
  }

  createTaxCategory(countryId: string, payload: TaxCategorySavePayload) {
    const url = `${this.apiUrl}/${countryId}/tax-categories`;
    return this.httpClient
      .post<{ success: boolean; message: string }>(url, this.toApiPayload(payload))
      .pipe(
        map((res) => ({
          message: res.message || 'Categoría fiscal creada',
        })),
      );
  }

  updateTaxCategory(
    countryId: string,
    id: string | number,
    payload: TaxCategorySavePayload,
  ) {
    const url = `${this.apiUrl}/${countryId}/tax-categories/${id}`;
    return this.httpClient
      .put<{ success: boolean; message: string }>(url, this.toApiPayload(payload))
      .pipe(
        map((res) => ({
          message: res.message || 'Categoría fiscal actualizada',
        })),
      );
  }

  deleteTaxCategory(countryId: string, id: string | number) {
    const url = `${this.apiUrl}/${countryId}/tax-categories/${id}`;
    return this.httpClient
      .delete<{ success: boolean; message: string }>(url)
      .pipe(
        map((res) => ({
          message: res.message || 'Categoría fiscal eliminada',
        })),
      );
  }

  /**
   * API flatten: body usa name (+ typeOfTaxpayerIds opcional).
   */
  private toApiPayload(payload: TaxCategorySavePayload): {
    name: string;
    code?: string;
    prefix?: string;
    status?: string;
    typeOfTaxpayerIds?: string[];
  } {
    const body: {
      name: string;
      code?: string;
      prefix?: string;
      status?: string;
      typeOfTaxpayerIds?: string[];
    } = {
      name: (payload.title ?? payload.name ?? '').trim(),
    };
    if (payload.status != null && payload.status !== '') {
      body.status = String(payload.status).toUpperCase();
    }
    if (payload.typeOfTaxpayerIds !== undefined) {
      body.typeOfTaxpayerIds = payload.typeOfTaxpayerIds;
    }
    return body;
  }
}

export interface TaxCategoryTaxpayer {
  id: string;
  prefix?: string;
  name?: string;
}

export interface TaxCategorySavePayload {
  name?: string;
  title?: string;
  status?: string;
  typeOfTaxpayerIds?: string[];
}

export interface TaxCategory {
  id: string;
  name: string;
  description?: string;
  relations?: number;
  typeOfTaxpayers?: TaxCategoryTaxpayer[];
  typeOfTaxpayerIds?: string[];
  status?: string;
  createdAt?: string;
}

export interface TaxCategoryPageResponse {
  items: TaxCategory[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}
